import React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, ShoppingCart, Package, BarChart3, RotateCcw,
  Settings, LogOut, Store, Bell, Users, AlertTriangle, Boxes
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useShop } from "../contexts/ShopContext";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "./ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";

const NavItem = ({ to, icon: Icon, label, testid }) => {
  const loc = useLocation();
  const active = loc.pathname.startsWith(to);
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors duration-200 text-sm
        ${active ? "bg-terracotta text-white" : "text-ink hover:bg-surface-alt"}`}
    >
      <Icon size={18} />
      <span className="font-medium">{label}</span>
    </Link>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { shops, activeShop, activeShopId, setShop } = useShop();

  return (
    <div className="min-h-screen bg-sand flex">
      {/* Sidebar */}
      <aside className="w-60 bg-white border-r border-border flex flex-col" data-testid="main-sidebar">
        <div className="px-5 py-5 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-terracotta flex items-center justify-center text-white font-heading font-bold">M</div>
            <div>
              <div className="font-heading font-bold text-ink leading-tight">MDM Bill</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Software · POS</div>
            </div>
          </div>
        </div>

        <div className="px-3 py-3 border-b border-border">
          <div className="label-overline text-muted-foreground mb-2 px-1">Active Shop</div>
          <Select value={activeShopId} onValueChange={setShop}>
            <SelectTrigger data-testid="shop-selector" className="bg-sand border-border h-auto py-2">
              <Store size={14} className="mr-2 text-terracotta flex-shrink-0" />
              <div className="flex flex-col items-start overflow-hidden flex-1 text-left">
                <span className="font-medium truncate w-full">{activeShop?.name || "Select shop"}</span>
                {activeShop?.address && (
                  <span className="text-[10px] text-muted-foreground truncate w-full">{activeShop.address}</span>
                )}
              </div>
            </SelectTrigger>
            <SelectContent>
              {shops.map((s) => (
                <SelectItem key={s.shop_id} value={s.shop_id} data-testid={`shop-option-${s.shop_id}`}>
                  <div className="flex flex-col">
                    <span>{s.name}</span>
                    {s.address && <span className="text-[10px] text-muted-foreground">{s.address}</span>}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <nav className="flex-1 px-3 py-3 space-y-1">
          {user?.role === "admin" && (
            <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" />
          )}
          <NavItem to="/pos" icon={ShoppingCart} label="POS / Billing" testid="nav-pos" />
          <NavItem to="/returns" icon={RotateCcw} label="Returns" testid="nav-returns" />
          {user?.role === "admin" ? (
            <NavItem to="/products" icon={Package} label="Products" testid="nav-products" />
          ) : null}
          {user?.role === "admin" && (
            <>
              <NavItem to="/customers" icon={Users} label="Customers" testid="nav-customers" />
              <NavItem to="/alerts" icon={AlertTriangle} label="Alerts" testid="nav-alerts" />
              <NavItem to="/reports" icon={BarChart3} label="Reports" testid="nav-reports" />
              <NavItem to="/bulk-orders" icon={Boxes} label="Bulk Orders" testid="nav-bulk-orders" />
              <NavItem to="/settings" icon={Settings} label="Settings" testid="nav-settings" />
            </>
          )}
        </nav>

        <div className="border-t border-border p-3 flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src={user?.picture} />
            <AvatarFallback>{user?.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</div>
            <Badge variant="outline" className="text-[10px] uppercase tracking-widest border-terracotta/40 text-terracotta">
              {user?.role}
            </Badge>
          </div>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn">
            <LogOut size={16} />
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
